import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, MapPin, Users, Briefcase, UserCog, Mail, Phone, ToggleRight, MessageSquareWarning, LogIn, PauseCircle, PlayCircle, Trash2, Loader2, MoreHorizontal, Clock, Layers } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { DeleteCompanyDialog } from "./DeleteCompanyDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FeaturesDialog } from "./FeaturesDialog";
import { ModulesDialog } from "./ModulesDialog";
import { modulesSummary } from "@/lib/modules";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const when = (iso) => (iso ? new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");

const Stat = ({ icon: Icon, label, value, testid }) => (
  <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={testid}><Icon className="w-3.5 h-3.5" /><span className="font-mono font-semibold text-foreground">{value}</span> {label}</span>
);

const BILLING = {
  free: () => ["Free forever", "bg-emerald-500/15 text-emerald-700"],
  trial: (b) => [`Trial · ${b.days_left} day${b.days_left === 1 ? "" : "s"} left`, b.ending_soon ? "bg-amber-500/20 text-amber-800" : "bg-secondary text-muted-foreground"],
  active: (b) => [`${b.plan_name} · ${b.cadence}${b.cancel_date ? " · ending" : ""}`, "bg-primary/10 text-primary"],
  past_due: () => ["Payment failed", "bg-destructive/10 text-destructive"],
  locked: () => ["Locked · no plan", "bg-destructive/10 text-destructive"],
};
const BillingTag = ({ c }) => {
  if (!c.billing) return null;
  const [label, cls] = BILLING[c.billing.status](c.billing);
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`} data-testid={`owner-company-billing-${c.id}`}>{label}{c.billing.founding ? " · founding" : ""}</span>;
};

const EndingSoon = ({ companies }) => {
  const soon = companies.filter((c) => c.billing?.ending_soon).sort((a, b) => a.billing.days_left - b.billing.days_left);
  if (!soon.length) return null;
  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3" data-testid="owner-trials-ending">
      <Clock className="w-5 h-5 text-amber-700 mt-0.5 shrink-0" />
      <div className="text-sm">
        <p className="font-semibold text-amber-900 dark:text-amber-200">{soon.length} free trial{soon.length === 1 ? " ends" : "s end"} within 7 days</p>
        <p className="text-amber-900/80 dark:text-amber-200/80">{soon.map((c) => `${c.name} (${c.billing.days_left} day${c.billing.days_left === 1 ? "" : "s"})`).join(" · ")}</p>
      </div>
    </div>
  );
};

const Logo = ({ c }) => (c.logo_url
  ? <img src={`${BACKEND}${c.logo_url}`} alt="" className="w-11 h-11 rounded-lg object-contain bg-white border border-border" />
  : <span className="w-11 h-11 rounded-lg grid place-items-center text-white font-display font-bold" style={{ background: c.color }}>{(c.name || "?").slice(0, 2).toUpperCase()}</span>);

const Actions = ({ c, onFeatures, onModules, onPause, onDelete }) => {
  const { enterCompany } = useAuth();
  const [busy, setBusy] = useState(false);
  const enter = async () => {
    setBusy(true);
    try { await enterCompany(c.id); } catch (e) { toast.error(e?.response?.data?.detail || "Could not open that company"); setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button size="sm" onClick={enter} disabled={busy} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid={`owner-company-enter-${c.id}`}>
        {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <LogIn className="w-4 h-4 mr-1.5" />} Open dashboard
      </Button>
      <Button variant="outline" size="sm" onClick={() => onFeatures(c)} className="rounded-full" data-testid={`owner-company-features-${c.id}`}>
        <ToggleRight className="w-4 h-4 mr-1.5" /> Extras{Object.values(c.features || {}).filter(Boolean).length ? ` (${Object.values(c.features).filter(Boolean).length} on)` : ""}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="rounded-full h-9 w-9" aria-label="More" data-testid={`owner-company-more-${c.id}`}><MoreHorizontal className="w-4 h-4" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onModules(c)} data-testid={`owner-company-modules-${c.id}`}>
            <Layers className="w-4 h-4 mr-2" /> Choose features…
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onPause(c)} data-testid={`owner-company-pause-${c.id}`}>
            {c.paused ? <><PlayCircle className="w-4 h-4 mr-2" /> Restart access</> : <><PauseCircle className="w-4 h-4 mr-2" /> Pause access</>}
          </DropdownMenuItem>
          {!c.legacy && (
            <DropdownMenuItem onClick={() => onDelete(c)} className="text-destructive focus:text-destructive" data-testid={`owner-company-delete-${c.id}`}>
              <Trash2 className="w-4 h-4 mr-2" /> Delete company…
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const CompanyCard = ({ c, ...actions }) => {
  return (
    <div className={`bg-card border rounded-xl p-5 space-y-4 ${c.paused ? "border-destructive/40" : "border-border"}`} data-testid={`owner-company-${c.id}`}>
      <div className="flex items-start gap-3">
        <Logo c={c} />
        <div className="min-w-0 flex-1">
          <p className="font-display font-bold text-lg leading-tight truncate" data-testid={`owner-company-name-${c.id}`}>{c.name}</p>
          <p className="text-xs text-muted-foreground">Joined {when(c.created_at)}{c.legacy ? " · Original company" : ""}{!c.setup_done ? " · Setup not finished" : ""}</p>
        </div>
        <BillingTag c={c} />
        {c.paused && <span className="rounded-full bg-destructive/10 text-destructive px-2 py-0.5 text-xs font-semibold" data-testid={`owner-company-paused-${c.id}`}>Paused</span>}
        {c.open_requests > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-700 px-2 py-0.5 text-xs font-semibold" data-testid={`owner-company-open-${c.id}`}><MessageSquareWarning className="w-3.5 h-3.5" />{c.open_requests}</span>}
      </div>
      <div className="text-sm space-y-1">
        <p className="font-medium">{c.owner_name || "—"}</p>
        {c.owner_email && <a href={`mailto:${c.owner_email}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary"><Mail className="w-3.5 h-3.5" />{c.owner_email}</a>}
        {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-muted-foreground hover:text-primary"><Phone className="w-3.5 h-3.5" />{c.phone}</a>}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        <Stat icon={MapPin} label={c.depots.length === 1 ? "depot" : "depots"} value={c.depots.length} testid={`owner-company-depots-${c.id}`} />
        <Stat icon={UserCog} label="logins" value={c.logins} />
        <Stat icon={Users} label="crew" value={c.crew} />
        <Stat icon={Briefcase} label={`jobs (${c.active_jobs} active)`} value={c.jobs} />
      </div>
      {c.modules && (
        <button type="button" onClick={() => actions.onModules(c)} className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium hover:bg-muted transition-colors" data-testid={`owner-company-modules-summary-${c.id}`}>
          <Layers className="w-3.5 h-3.5 text-primary" /> {modulesSummary(c.modules)}
        </button>
      )}
      {c.depots.length > 0 && <p className="text-xs text-muted-foreground truncate">{c.depots.join(" · ")}</p>}
      <Actions c={c} {...actions} />
    </div>
  );
};

export const CompaniesPanel = ({ companies, setCompanies }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [modulesFor, setModulesFor] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const pause = async (c) => {
    try {
      const r = await api.ownerPause(c.id, !c.paused);
      setCompanies((cs) => cs.map((x) => (x.id === c.id ? { ...x, paused: r.paused } : x)));
      toast.success(r.paused ? `${c.name} paused — nobody there can sign in` : `${c.name} restarted`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not change access"); }
  };
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? companies.filter((c) => [c.name, c.owner_name, c.owner_email, ...c.depots].join(" ").toLowerCase().includes(s)) : companies;
  }, [q, companies]);
  const changed = (id, key, enabled) => {
    const upd = (c) => (c.id === id ? { ...c, features: { ...c.features, [key]: enabled } } : c);
    setCompanies((cs) => cs.map(upd));
    setOpen((c) => (c ? upd(c) : c));
  };

  return (
    <div className="space-y-5" data-testid="owner-companies">
      <EndingSoon companies={companies} />
      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search companies, people or depots" className="pl-9" data-testid="owner-company-search" />
      </div>
      {shown.length === 0
        ? <div className="text-center py-16 border border-dashed border-border rounded-2xl text-muted-foreground">No companies match.</div>
        : <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">{shown.map((c) => <CompanyCard key={c.id} c={c} onFeatures={setOpen} onModules={setModulesFor} onPause={pause} onDelete={setToDelete} />)}</div>}
      <FeaturesDialog company={open} onClose={() => setOpen(null)} onChanged={changed} />
      <ModulesDialog company={modulesFor} onClose={() => setModulesFor(null)} onSaved={(id, modules) => setCompanies((cs) => cs.map((x) => (x.id === id ? { ...x, modules } : x)))} />
      <DeleteCompanyDialog company={toDelete} onClose={() => setToDelete(null)} onDeleted={(id) => { setToDelete(null); setCompanies((cs) => cs.filter((x) => x.id !== id)); }} />
    </div>
  );
};
